import React, { useState } from 'react';
import type { HomeProps } from '../../../shells/types';

type CrewTab = 'all' | 'unread' | 'friends' | 'channels' | 'helping';

const TABS: { id: CrewTab; label: string; testId: string }[] = [
  { id: 'all',      label: 'All',      testId: 'crew-tab-all' },
  { id: 'unread',   label: 'Unread',   testId: 'crew-tab-unread' },
  { id: 'friends',  label: 'Friends',  testId: 'crew-tab-friends' },
  { id: 'channels', label: 'Channels', testId: 'crew-tab-channels' },
  { id: 'helping',  label: 'Helping',  testId: 'crew-tab-helping' },
];

/**
 * Phase 73 full — Teens C · Pocket Crew Home surface.
 *
 * Messaging-first home with a tab strip. The "Helping" tab is the
 * civic-mutual-aid first-class destination: it bridges to env-board
 * (open jobs nearby). Other tabs bridge into the chats/feed family.
 * Shell is reskin only — surfaces unchanged.
 */
export function CrewHome({ ctx }: HomeProps): JSX.Element {
  const [tab, setTab] = useState<CrewTab>('all');
  const go = (target: string, reason: string) => {
    ctx.director.bridge(target, reason);
  };

  const onTab = (next: CrewTab) => {
    setTab(next);
    // Helping tab is the civic destination — bridge straight to the
    // board so the user lands on open jobs without a second click.
    if (next === 'helping') go('env-board', 'crew-helping');
  };

  return (
    <div className="crew-home" data-testid="crew-home">
      <div className="crew-header">
        <span className="crew-brand">pocket<span className="crew-brand-accent">crew</span></span>
        <input
          className="crew-search"
          placeholder="Search · jump to a chat"
          data-testid="crew-search"
          onClick={() => go('sass-chats', 'crew-search')}
          readOnly
        />
        <button
          type="button"
          className="crew-sos"
          data-testid="crew-sos"
          onClick={() => go('env-intake', 'crew-sos')}
        >
          SOS
        </button>
      </div>

      <div className="crew-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            data-testid={t.testId}
            data-tab={t.id}
            aria-selected={tab === t.id}
            className="crew-tab"
            onClick={() => onTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="crew-list">
        {tab === 'all' && (
          <>
            <CrewRow initials="DM" title="Direct messages" preview="open the messages surface" onClick={() => go('sass-chats', 'crew-row-dm')} />
            <CrewRow initials="📞" title="Voice + video" preview="recent calls + start" onClick={() => go('env-voice', 'crew-row-voice')} />
            <CrewRow initials="📰" title="News" preview="town headlines · BBC · NPR" onClick={() => go('news-paper', 'crew-row-news')} />
            <CrewRow initials="🤝" title="Help nearby" preview="see what your neighbors need" onClick={() => go('env-board', 'crew-row-help')} />
            <CrewRow initials="📡" title="Feed" preview="bulletins + stories + posts" onClick={() => go('env-social', 'crew-row-feed')} />
          </>
        )}
        {tab === 'unread' && (
          <div className="crew-empty">All caught up. Tap All to see everything.</div>
        )}
        {tab === 'friends' && (
          <CrewRow initials="C" title="Contacts" preview="open your contact list" onClick={() => go('contact-picker', 'crew-row-contacts')} />
        )}
        {tab === 'channels' && (
          <CrewRow initials="📡" title="Channels + bulletins" preview="public broadcasts in your area" onClick={() => go('social-channel', 'crew-row-channel')} />
        )}
        {tab === 'helping' && (
          <div className="crew-empty">Opening the help board…</div>
        )}
      </div>
    </div>
  );
}

function CrewRow({ initials, title, preview, onClick }: {
  initials: string; title: string; preview: string; onClick: () => void;
}): JSX.Element {
  return (
    <div role="button" tabIndex={0} className="crew-row" onClick={onClick} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}>
      <span className="crew-row-avatar" aria-hidden>{initials}</span>
      <div className="crew-row-body">
        <span className="crew-row-title">{title}</span>
        <span className="crew-row-preview">{preview}</span>
      </div>
    </div>
  );
}
